import DataLayer from '@services/data-layer/index'
import getAndUpdate from '@utils/get-and-update'
import { getCookie, deleteCookie } from '@utils/cookies'
import localSave from '@utils/local-save'
import analyticsService from './analytics-service'
import settingsService from './settings-service'
import { onDemandQualityOptions } from '../configuration'

const CHECK_VERSION_INTERVAL_DURATION = 1000 * 60 * 60 // 1 hour

class AppInitializer {
    constructor({ store, router, dataLayer, messages, features = {} }) {
        this.store = store
        this.router = router
        this.dataLayer = dataLayer
        this.messages = messages
        this.checkVersionInterval = null

        this.features = {
            auth: true,
            channels: true,
            versionCheck: true,
            premium: true,
            settings: true,
            favouritesOnboarding: true,
            premiumOnboarding: true,
            ...features,
        }
    }

    isFeatureEnabled(key) {
        return !!this.features[key]
    }

    enableFeature(key) {
        this.features[key] = true
    }

    disableFeature(key) {
        this.features[key] = false

        if (key === 'versionCheck' && this.checkVersionInterval) {
            clearInterval(this.checkVersionInterval)
            this.checkVersionInterval = null
        }
    }

    setFeatures(newFeatures = {}) {
        this.features = Object.assign(this.features, newFeatures)
    }

    get isLoggedIn() {
        return this.store.getters['auth/loggedIn']
    }

    get isPremium() {
        return this.store.getters['premium/isPremium']
    }

    get currentUser() {
        return this.store.state.auth.currentUser?.members_id
    }

    async initialize() {
        if (this.isFeatureEnabled('auth')) this.checkAuthentication()
        if (this.isFeatureEnabled('channels')) this.initChannels()
        if (this.isFeatureEnabled('versionCheck')) this.startCodeVersionCheck()
        if (this.isFeatureEnabled('premium')) await this.initPremium()
        if (this.isFeatureEnabled('settings')) await this.initSettings()
        if (this.isFeatureEnabled('favouritesOnboarding')) return this.initOnboarding()
        if (this.isFeatureEnabled('premiumOnboarding')) await this.handlePremiumOnboarding()
    }

    // Authentication Methods
    checkAuthentication() {
        if (!this.isFeatureEnabled('auth')) return

        const oauthSession = getCookie('oauthSession') || ''
        if (oauthSession) {
            const user = JSON.parse(oauthSession)
            this.store.dispatch('auth/setUser', user)
            this.store.dispatch('member/init', null, { root: true })
            deleteCookie({ name: 'oauthSession', domain: process.env.VUE_APP_COOKIES_DOMAIN })

            const redirectURL = localSave.get('redirectURL') || '/'
            window.location.replace(redirectURL)
        }
    }

    // Channel Initialization
    initChannels() {
        if (!this.isFeatureEnabled('channels')) return

        getAndUpdate(
            channels => this.store.commit('channels/SET_CHANNELS', channels),
            this.dataLayer.channels.get()
        )
    }

    // Code Version Checking
    startCodeVersionCheck() {
        if (!this.isFeatureEnabled('versionCheck')) return

        this.checkCodeVersion()
        this.checkVersionInterval = setInterval(() => {
            this.checkCodeVersion()
        }, CHECK_VERSION_INTERVAL_DURATION)
    }

    async checkCodeVersion() {
        if (!this.isFeatureEnabled('versionCheck')) return

        const localCodeVersion = localSave.get('localCodeVersion')
        try {
            const response = await DataLayer.versions.get()
            const actualCodeVersion = response.body.clientVersion
            localSave.save('actualCodeVersion', actualCodeVersion)

            if (!localCodeVersion) localSave.save('localCodeVersion', actualCodeVersion)
            else if (localCodeVersion !== actualCodeVersion) {
                this.notifyForNewVersion(localCodeVersion, response.body)
            }
        } catch (error) {
            console.error('Failed to check code version:', error)
        }
    }

    notifyForNewVersion(oldVersion, newVersion) {
        const messageKey = newVersion.severity === 'critical'
            ? 'CRITICAL_UPDATE_AVAILABLE'
            : 'UPDATE_AVAILABLE'

        this.store.dispatch('appearance/handleNotification', {
            message: this.messages(messageKey),
            type: 'warning',
            hasReload: true,
            isPersistentNotification: true,
            forceUpdate: newVersion.severity === 'critical',
        })
    }

    async handlePremiumLoad() {
        if(!this.isFeatureEnabled('premium')) return
        if(!this.isLoggedIn) return
        if (!localStorage.getItem('hasAppLoaded') && !this.isPremium) {
            localStorage.setItem('hasAppLoaded', true)
            if (window.location.pathname.includes('premium')) return
            analyticsService.premiumUpsellInitiated('non-premium-log-in')
            this.router.push({ path: '/premium' })
        }
    }

    // Premium Initialization
    async initPremium() {
        if (!this.isFeatureEnabled('premium')) return

        const premiumService = DataLayer.subscriptions
        const promises = [premiumService.getProducts()]
        const memberId = this.store.state.auth.currentUser?.members_id?.id
        if (this.isLoggedIn) promises.push(premiumService.getSubscriptionsByMemberId(memberId))
        const [plans, subscriptions = null] = await Promise.all(promises)
        this.store.commit('premium/SET_PLANS', plans.body)
        if (subscriptions) {
            this.store.commit('premium/SET_SUBSCRIPTIONS', subscriptions.body)
            const activeSubscription = this.store.getters['premium/activeSubscription']
            if(activeSubscription?.subscriptionsCheckoutId?.id) {
                const transaction = await premiumService.getCheckoutById(activeSubscription.subscriptionsCheckoutId.id)
                this.store.commit('premium/SET_ACTIVE_TRANSACTION', transaction.body)
            }
        }
        this.store.commit('premium/SET_IS_LOADING', false)
        await this.handlePremiumLoad()
    }

    async handleActiveSubscription() {
        if (!this.isFeatureEnabled('premium')) return

        const activeSubscription = this.store.getters['premium/activeSubscription']
        if (!activeSubscription) return

        const transaction = await DataLayer.subscriptions.getCheckoutById(
            activeSubscription.subscriptionsCheckoutId.id
        )
        this.store.commit('premium/SET_ACTIVE_TRANSACTION', transaction.body)
    }

    async handlePremiumOnboarding() {
        if (!this.isFeatureEnabled('premiumOnboarding')) return
        if (!this.isLoggedIn || localStorage.getItem('hasSkippedPremiumOnboarding') || this.isPremium) return

        if (!this.router.currentRoute.name.includes('premium')) {
            analyticsService.premiumUpsellInitiated('non-premium-log-in')
            await this.router.push({ path: '/premium' })
        }
    }

    // Settings Initialization
    async initSettings() {
        if (!this.isFeatureEnabled('settings')) return
        if (!this.isLoggedIn) return

        const savedQuality = await settingsService.getCurrentOnDemandQualityKey()
        const defaultQuality = this.isPremium ? 'high' : 'low'
        let onDemandQuality = savedQuality || defaultQuality

        // Validate that saved quality matches premium entitlement
        if (savedQuality) {
            const savedQualityOption = onDemandQualityOptions[savedQuality]

            // If user has a premium quality saved but is no longer premium, reset to default
            if (savedQualityOption?.needsSub && !this.isPremium) {
                onDemandQuality = defaultQuality
                await settingsService.setCurrentOnDemandQuality(onDemandQuality)
            }
        } else {
            // No saved preference, set default
            await settingsService.setCurrentOnDemandQuality(onDemandQuality)
        }

        this.store.commit('settings/SET_SETTINGS', { currentOnDemandQualityKey: onDemandQuality })
    }

    // Favorites onboarding
    async initFavorites() {
        if(!this.currentUser) return null

        const favoritesService = DataLayer.favorites
        const currentUserId = this.currentUser.id

        const [artists, genres] = await Promise.all([
            favoritesService.getAll({type: 'artists', membersId: currentUserId }),
            favoritesService.getAll({type: 'genres', membersId: currentUserId }),
        ])

        return {
            artists,
            genres
        }
    }

    async initOnboarding() {
        if (!this.isFeatureEnabled('favouritesOnboarding')) return false
        if(!this.currentUser) return false

        const MINIMUM_COUNT = 3
        const ONBOARDING_PATH = 'onboarding'
        const favorites = await this.initFavorites()
        if (!favorites) return false

        const { artists, genres } = favorites
        const shouldOnboard = artists.body.length < MINIMUM_COUNT && genres.body.length < MINIMUM_COUNT
        const isOnboarding = this.router.currentRoute.name === ONBOARDING_PATH

        if(!shouldOnboard) return false

        else {
            if(isOnboarding) return true
            await this.router.push({ name: ONBOARDING_PATH })
            return true
        }
    }

    // Cleanup
    destroy() {
        if (this.checkVersionInterval) clearInterval(this.checkVersionInterval)
    }
}

export default AppInitializer
